import { TAccordionItemInstance } from 'widgets/global/AccordionItem';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';

type TData = {
    fieldName: string;
    errorMsg: string;
};

/**
 * @description General type for for payment option
 */
export interface IPaymentOptionAccordionItem extends TAccordionItemInstance {
    prefs(): ReturnType<TAccordionItemInstance['prefs']> & {
        methodId: string;
        methodName: string;
        skipBillingAddress: boolean;
    };
}

/**
 * @description PaymentAccordionItem implementation
 * @param AccordionItem widget
 * @returns Payment Tab Panel class
 */
function PaymentAccordionItemClassCreator(AccordionItem: import('widgets/global/AccordionItem').TAccordionItem) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class PaymentAccordionItem
     * @augments AccordionItem
     * @classdesc Payment Accordion Item general behavior.
     * Uses as a parent of the children payment widget. For example {@link PaymentAccordionItemCREDITCARD}
     *
     * Has next features:
     * 1. Opens active payment panel on page load \ widget reload
     * 2. General payment processing handler
     * 3. General error handling of the current payment accordion item
     * 4. General validation of the current payment accordion item
     *
     * Widget should be not used as standalone widget, it should be mixed to other widgets (see {@tutorial WidgetsMixinsStrategy})
     *
     * @property {boolean} data-expanded - Expanded flag
     * @property {string} data-method-name - payment method name
     */
    class PaymentAccordionItem extends AccordionItem implements IPaymentOptionAccordionItem {
        prefs() {
            return {
                methodId: '',
                methodName: '',
                isDynamicPayment: false,
                skipBillingAddress: false,
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();

            if (this.prefs().expanded) {
                this.openPanel();
            }
        }

        /**
         * @description Handles payment tab submit.
         * Each payment should implement its own "process" method
         * @param attributes needed attributes for payment item
         * @returns result
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        process(attributes: Record<string, unknown>): Promise<unknown> {
            return new Promise((resolve, reject) => {
                reject(new Error('No payment'));
            });
        }

        /**
         * @description Event listener for errors, used for updating accordion height if error message is too long.
         */
        onSetError() {
            setTimeout(() => this.updateState(), 0);
        }

        /**
         * @description Handle panel errors
         * @param data - data object
         */
        handleError(data: TData) {
            if (!data.fieldName) {
                return;
            }

            this.getById(data.fieldName, (field: TBasicInputInstance) => {
                if (field.id === data.fieldName) {
                    field.setError(data.errorMsg);

                    if (field.setFocus) {
                        field.setFocus();
                    }
                }
            });
        }

        /**
         * @description Validate child elements.
         * Each payment should implement its own "isChildrenValid" method
         *
         * @returns result
         */
        isChildrenValid(): boolean {
            return false;
        }

        /**
         * @description On widget Refresh Handler
         */
        onRefresh() {
            super.onRefresh();

            if (this.isPanelOpen) {
                this.openPanel();
            }
        }

        /**
         * @description Opens payment accordion panel and toggle Billing Form, if Billing Form has to be presented for chosen Payment Accordion Item
         *
         * @emits AccordionItem#closeallitems
         * @emits "checkout.paymentmethod.updated"
         * @emits PaymentAccordionItem#togglebillingform
         */
        openPanel() {
            super.openPanel();

            const hasToBeHidden = this.prefs().skipBillingAddress;

            this.emit('togglebillingform', !hasToBeHidden);

            if (this.isPanelOpen) {
                this.eventBus().emit('checkout.paymentmethod.updated', {
                    name: this.prefs().methodName,
                    id: this.prefs().methodId
                });
            }
        }

        /**
        * @description Handles panel close
        * @emits PaymentAccordionItem#togglebillingform
        * @param skipEvent Skip event marker
        */
        closePanel(skipEvent?: boolean) {
            super.closePanel();

            if (!skipEvent) {
                this.emit('togglebillingform', false);
            }
        }

        /**
         * @description Clears sensitive data
         * @param stage Stage on which data is going to be cleared
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        cleanupSensitiveData(stage: string) {
            // Placeholder for logic on cleaning up customer sensitive data
        }
    }

    return PaymentAccordionItem;
}

export type TPaymentAccordionItem = ReturnType<typeof PaymentAccordionItemClassCreator>;

export type TPaymentAccordionItemInstance = InstanceType<TPaymentAccordionItem>;

export default PaymentAccordionItemClassCreator;
